import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";

const EditDeliveryDialog = ({
  open,
  onClose,
  deliveryName,
  token,
  onSuccess,
}) => {
  const [menuItems, setMenuItems] = useState({});
  const [updatedItemPrices, setUpdatedItemPrices] = useState({});
  const [updatedOptionPrices, setUpdatedOptionPrices] = useState({});
  const [search, setSearch] = useState("");
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const getOptionDeliveryPrice = (choice, dName) => {
    const match =
      Array.isArray(choice?.deliveries) &&
      choice.deliveries.find((d) => d.deliveryName === dName);
    if (match && typeof match.deliveryPrice === "number")
      return match.deliveryPrice;
    return typeof choice?.price === "number" ? choice.price : 0;
  };

  const makeOptKey = (itemId, groupName, choiceName) =>
    `${itemId}||${groupName}||${choiceName}`;

  useEffect(() => {
    const fetchMenuItems = async () => {
      setLoadingMenu(true);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/fetch-menu-with-archive`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const allItems = [
          ...(data.menu?.menuItems || []),
          ...((data.menu?.menuArchive || []).map((archived) => ({
            ...archived,
            isArchived: true,
          })) || []),
        ];

        const grouped = {};
        allItems.forEach((item) => {
          const category = item.categoryName || "Archive";
          const delivery = item.deliveries?.find(
            (d) => d.deliveryName === deliveryName
          );

          const entry = {
            _id: item._id,
            menuName: item.menuName,
            basePrice: item.basePrice,
            isArchived: !!item.isArchived,
            deliveryPrice: delivery ? delivery.deliveryPrice : 0,
            options: Array.isArray(item.options)
              ? item.options.map((g) => ({
                  optionName: g.optionName,
                  optionType: g.optionType,
                  choices: (g.choices || []).map((c) => ({
                    name: c.name,
                    price: typeof c.price === "number" ? c.price : 0,
                    deliveries: Array.isArray(c.deliveries) ? c.deliveries : [],
                    currentDeliveryPrice: getOptionDeliveryPrice(
                      c,
                      deliveryName
                    ),
                  })),
                }))
              : [],
          };

          if (!grouped[category]) grouped[category] = [];
          grouped[category].push(entry);
        });

        setMenuItems(grouped);
      } catch (err) {
        console.error("Error fetching menu items:", err);
      } finally {
        setLoadingMenu(false);
      }
    };

    if (open) fetchMenuItems();
  }, [open, deliveryName, token]);

  const handleItemPriceChange = (id, value) => {
    const num = value === "" ? "" : Number(value);
    setUpdatedItemPrices((prev) => ({ ...prev, [id]: num }));
  };

  const handleOptionPriceChange = (itemId, groupName, choiceName, value) => {
    const key = makeOptKey(itemId, groupName, choiceName);
    const num = value === "" ? "" : Number(value);
    setUpdatedOptionPrices((prev) => ({ ...prev, [key]: num }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    const itemLevelPayload = Object.entries(updatedItemPrices)
      .filter(([, price]) => price !== "" && !Number.isNaN(Number(price)))
      .map(([menuItemId, price]) => ({
        menuItemId,
        deliveryName,
        deliveryPrice: Number(price),
      }));

    const optionLevelPayload = Object.entries(updatedOptionPrices)
      .filter(([, price]) => price !== "" && !Number.isNaN(Number(price)))
      .map(([key, price]) => {
        const [menuItemId, optionGroupName, choiceName] = key.split("||");
        return {
          menuItemId,
          optionGroupName,
          choiceName,
          deliveryName,
          optionDeliveryPrice: Number(price),
        };
      });

    const payload = [...itemLevelPayload, ...optionLevelPayload];

    if (payload.length === 0) {
      setSubmitting(false);
      onClose();
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/update-delivery-prices`,
        { updates: payload },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error updating delivery prices:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderOptionsBlock = (item) => {
    if (!item.options?.length) return null;

    const searchLC = (search || "").toLowerCase();
    const showGroup = (g) =>
      g.optionName?.toLowerCase().includes(searchLC) ||
      g.choices?.some((c) => c.name?.toLowerCase().includes(searchLC));

    const visibleGroups = item.options.filter(showGroup);
    if (!visibleGroups.length) return null;

    return (
      <Table size="small" sx={{ mt: 1, mb: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: 2 }}>Option Group</TableCell>
            <TableCell>Choice</TableCell>
            <TableCell align="right" sx={{ width: 140 }}>
              Choice Base Price
            </TableCell>
            <TableCell align="right" sx={{ width: 200 }}>
              {`Choice Delivery Price — ${deliveryName}`}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleGroups.map((g) =>
            g.choices.map((c) => {
              const key = makeOptKey(item._id, g.optionName, c.name);
              const currentValue =
                updatedOptionPrices[key] !== undefined
                  ? updatedOptionPrices[key]
                  : c.currentDeliveryPrice;
              return (
                <TableRow key={key}>
                  <TableCell sx={{ pl: 2 }}>{g.optionName}</TableCell>
                  <TableCell>{c.name}</TableCell>
                  <TableCell align="right">
                    ₱{(c.price || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      value={currentValue}
                      onChange={(e) =>
                        handleOptionPriceChange(
                          item._id,
                          g.optionName,
                          c.name,
                          e.target.value
                        )
                      }
                      inputProps={{ min: 0, step: "0.01" }}
                      disabled={submitting}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Edit Delivery Prices for “{deliveryName}”</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Search by name, category, option group, or choice"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          margin="dense"
          disabled={loadingMenu}
        />

        {loadingMenu ? (
          <Box
            sx={{ display: "flex", alignItems: "center", mt: 3, mb: 2, gap: 2 }}
          >
            <CircularProgress size={24} />
            <Typography>Fetching Menu...</Typography>
          </Box>
        ) : (
          Object.entries(menuItems)
            .map(([category, items]) => {
              const s = (search || "").toLowerCase();
              const filteredItems = items.filter((item) => {
                const nameMatch = item.menuName?.toLowerCase().includes(s);
                const categoryMatch = category?.toLowerCase().includes(s);
                const optionMatch = item.options?.some(
                  (g) =>
                    g.optionName?.toLowerCase().includes(s) ||
                    g.choices?.some((c) => c.name?.toLowerCase().includes(s))
                );
                return nameMatch || categoryMatch || optionMatch;
              });
              return filteredItems.length > 0
                ? [category, filteredItems]
                : null;
            })
            .filter(Boolean)
            .map(([category, items]) => (
              <Accordion key={category} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>
                    {category}
                    {items.some((i) => i.isArchived) && " (Archived)"}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Menu Name</TableCell>
                        <TableCell align="right" sx={{ width: 140 }}>
                          Base Price
                        </TableCell>
                        <TableCell align="right" sx={{ width: 160 }}>
                          Delivery Price
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item) => {
                        const itemValue =
                          updatedItemPrices[item._id] !== undefined
                            ? updatedItemPrices[item._id]
                            : item.deliveryPrice;

                        return (
                          <React.Fragment key={item._id}>
                            <TableRow>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography>{item.menuName}</Typography>
                                  {!!item.options?.length && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        ml: 1,
                                        px: 1,
                                        py: 0.25,
                                        bgcolor: "action.hover",
                                        borderRadius: 1,
                                      }}
                                    >
                                      has options
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                ₱{(item.basePrice || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={itemValue}
                                  onChange={(e) =>
                                    handleItemPriceChange(
                                      item._id,
                                      e.target.value
                                    )
                                  }
                                  inputProps={{ min: 0, step: "0.01" }}
                                  disabled={submitting}
                                />
                              </TableCell>
                            </TableRow>

                            {!!item.options?.length && (
                              <TableRow>
                                <TableCell
                                  colSpan={3}
                                  sx={{ bgcolor: "background.default" }}
                                >
                                  <Divider sx={{ my: 1 }} />
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1 }}
                                  >
                                    Options (editable per choice)
                                  </Typography>
                                  {renderOptionsBlock(item)}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loadingMenu}
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Submit"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDeliveryDialog;
